import React, { useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { TrendingUp } from 'lucide-react';
import styles from './CustomValueChart.module.css';

interface CompletionData {
  date: string;
  completed: boolean;
  customValue?: string | number;
  notes?: string;
}

interface CustomValueChartProps {
  data: CompletionData[];
  customValueLabel?: string;
  customValueUnit?: string;
  className?: string;
}

const CustomValueChart: React.FC<CustomValueChartProps> = ({
  data,
  customValueLabel,
  customValueUnit,
  className
}) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Listener para mudanças de tamanho da tela
  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Filtrar apenas valores numéricos válidos e últimos 30 registros
  const chartData = data
    .filter(item => 
      item.completed && 
      item.customValue !== undefined && 
      item.customValue !== null &&
      typeof item.customValue === 'number' &&
      !isNaN(item.customValue as number)
    )
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .slice(-30) // Últimos 30 valores
    .map(item => ({
      date: item.date,
      value: item.customValue as number,
      formattedDate: format(new Date(item.date), 'dd/MM', { locale: ptBR }),
      fullDate: format(new Date(item.date), 'dd/MM/yyyy', { locale: ptBR })
    }));

  // Debug: Log dos dados recebidos e processados
  React.useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    const todayData = data.find(item => item.date === today);
    
    // Log mais detalhado para debugging
    console.log('📊 CustomValueChart - Debug COMPLETO:', {
      totalDataReceived: data.length,
      validNumericData: chartData.length,
      todayString: today,
      todayFound: !!todayData,
      todayData: todayData ? {
        date: todayData.date,
        completed: todayData.completed,
        customValue: todayData.customValue,
        customValueType: typeof todayData.customValue,
        isValidNumber: typeof todayData.customValue === 'number' && !isNaN(todayData.customValue as number)
      } : null,
      allDatesInData: data.map(item => ({
        date: item.date,
        completed: item.completed,
        hasCustomValue: item.customValue !== undefined && item.customValue !== null,
        customValue: item.customValue,
        customValueType: typeof item.customValue
      })).slice(-10), // Últimos 10 para ver o padrão
      filteredValidData: chartData.map(item => ({
        date: item.date,
        value: item.value
      }))
    });
  }, [data, chartData]);

  // Se não há dados suficientes, mostrar placeholder
  if (chartData.length === 0) {
    return (
      <div className={`${styles.container} ${className || ''}`}>
        <div className={styles.header}>
          <h4 className={styles.title}>
            <TrendingUp size={20} />
            <span>Evolução de {customValueLabel || 'Valores'}</span>
          </h4>
        </div>
        <div className={styles.placeholder}>
          <TrendingUp className={styles.placeholderIcon} />
          <p className={styles.placeholderText}>Nenhum dado disponível ainda</p>
          <p className={styles.placeholderSubtext}>
            Complete o hábito com valores numéricos para ver o gráfico
          </p>
        </div>
      </div>
    );
  }

  // Calcular domínio do eixo Y com margem de ~10%
  const values = chartData.map(item => item.value);
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const range = maxValue - minValue;
  const margin = Math.max(range * 0.1, 1); // Pelo menos 1 de margem

  const yAxisDomain = [
    Math.max(0, minValue - margin), // Não permitir valores negativos se o mínimo for positivo
    maxValue + margin
  ];

  // Tooltip customizado
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className={styles.tooltip}>
          <p className={styles.tooltipDate}>{data.fullDate}</p>
          <p className={styles.tooltipValue}>
            {customValueLabel || 'Valor'}: {payload[0].value} {customValueUnit || ''}
          </p>
        </div>
      );
    }
    return null;
  };

  // Versão mobile compacta (estilo TinyLineChart)
  if (isMobile) {
    return (
      <div className={`${styles.container} ${styles.mobileContainer} ${className || ''}`}>
        <div className={styles.mobileHeader}>
          <h4 className={styles.mobileTitle}>
            <TrendingUp size={16} />
            <span>{' ' + customValueLabel || 'Valores'}</span>
          </h4>
          <div className={styles.mobileInfo}>
            {chartData.length} registro{chartData.length !== 1 ? 's' : ''}
          </div>
        </div>
          <div className={styles.mobileChartWrapper}>
          <ResponsiveContainer width="100%" height={120}>
            <LineChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
              <Tooltip content={<CustomTooltip />} />
              <Line 
                type="monotone" 
                dataKey="value" 
                stroke="#3b82f6" 
                strokeWidth={2} 
                dot={{ 
                  fill: "#3b82f6", 
                  strokeWidth: 0,
                  r: 3
                }}
                activeDot={{ 
                  r: 4, 
                  fill: "#3b82f6",
                  stroke: "#ffffff",
                  strokeWidth: 1
                }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Métricas organizadas para mobile */}
        <div className={styles.mobileStats}>
          <div className={styles.mobileStatItem}>
            <span className={styles.mobileStatValue}>
              {values.reduce((a, b) => a + b, 0)} {customValueUnit || ''}
            </span>
            <span className={styles.mobileStatLabel}>SOMA</span>
          </div>
          <div className={styles.mobileStatItem}>
            <span className={styles.mobileStatValue}>
              {(values.reduce((a, b) => a + b, 0) / values.length).toFixed(1)} {customValueUnit || ''}
            </span>
            <span className={styles.mobileStatLabel}>MÉDIA</span>
          </div>
          <div className={styles.mobileStatItem}>
            <span className={styles.mobileStatValue}>
              {values.length}
            </span>
            <span className={styles.mobileStatLabel}>ENTRADAS</span>
          </div>
        </div>
      </div>
    );
  }

  // Versão desktop completa
  return (
    <div className={`${styles.container} ${className || ''}`}>
      <div className={styles.header}>
        <h4 className={styles.title}>
          <TrendingUp size={20} />
          <span> Evolução de {customValueLabel || 'Valores'}</span>
        </h4>
        <div className={styles.chartInfo}>
          {chartData.length} registro{chartData.length !== 1 ? 's' : ''}
        </div>
      </div>
      
      <div className={styles.chartWrapper}>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart
            data={chartData}
            margin={{
              top: 20,
              right: 10,
              left: 40,
              bottom: 60
            }}
          >
            <CartesianGrid 
              strokeDasharray="3 3" 
              className={styles.grid}
            />
            <XAxis 
              dataKey="formattedDate"
              angle={-45}
              textAnchor="end"
              height={60}
              fontSize={12}
              className={styles.xAxis}
              interval="preserveStartEnd"
            />
            <YAxis 
              domain={yAxisDomain}
              fontSize={12}
              className={styles.yAxis}
              label={{ 
                value: customValueUnit || '', 
                angle: -90, 
                position: 'insideLeft',
                offset: 10,
                className: styles.yAxisLabel
              }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Line
              type="monotone"
              dataKey="value"
              stroke="#3b82f6"
              strokeWidth={2}
              dot={{ 
                fill: "#3b82f6", 
                strokeWidth: 2,
                r: 4
              }}
              activeDot={{ 
                r: 6, 
                fill: "#3b82f6",
                stroke: "#ffffff",
                strokeWidth: 2
              }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Métricas organizadas */}
      <div className={styles.stats}>
        <div className={styles.statGroup}>
          <div className={styles.statGroupTitle}>SOMA</div>
          <div className={styles.statItem}>
            <span className={styles.statValue}>
              {values.reduce((a, b) => a + b, 0)} {customValueUnit || ''}
            </span>
          </div>
        </div>
        
        <div className={styles.statGroup}>
          <div className={styles.statGroupTitle}>MÉDIA</div>
          <div className={styles.statItem}>
            <span className={styles.statValue}>
              {(values.reduce((a, b) => a + b, 0) / values.length).toFixed(1)} {customValueUnit || ''}
            </span>
          </div>
        </div>
        
        <div className={styles.statGroup}>
          <div className={styles.statGroupTitle}>ENTRADAS</div>
          <div className={styles.statItem}>
            <span className={styles.statValue}>
              {values.length}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomValueChart;